import React, { useState, useEffect, useContext, useMemo } from 'react';
import CustomDialog from '../../components/ui/feedback/CustomDialog';
import { AssessmentContext } from '../../context/AssessmentContext.js';
import { WebSocketContext } from '../../context/WebSocketContext.js';
import * as WebSocketStatus from '../../context/WebSocketStatus.js';
import useSessionStorage from '../../hooks/useSessionStorage.js';
import * as TestStatus from './TestStatus.js';
import * as Storage from '../common/Storage.js';
import * as PwaEvents from '../../context/PwaEvents.js';
import { NETWORK_ERROR_ACTION } from '../../scripts/network-error/networkError.js';
import { checkReadyToResume } from '../../scripts/less-secure/enforceLessSecure.js';
import { PSYCHOMETRIC_EVENTS } from './PyschometricEvents.js'
import { constructMessage } from './WebsocketMessageHandler.js';
import { useTranslation } from 'react-i18next';

const WebsocketOverlays = () => {
  const {
    openErrorDialog,
    setOpenErrorDialog,
    setOpenContinueDialog,
    saveErrorCount,
    learnosityItemApp,
    updatePausedBy,
    setExit,
    setSubmitInProgress,
    learnosityRequestObj,
    loginData,
  } = useContext(AssessmentContext);
  const { socketStatus, sendMsgToWebsocket } = useContext(WebSocketContext);
  const { getStorageItem, setStorageItem } = useSessionStorage();
  const { t } = useTranslation();

  const storedTestStatus = getStorageItem(Storage.STORAGE_TEST_STATUS);
  const [awaitingConnection, setAwaitingConnection] = useState(
    TestStatus.TEST_PAUSED === storedTestStatus ? false : true
  );

  const __attemptNumber = getStorageItem(Storage.STORAGE_ATTEMPT_NO) || 0;

  const [connectDialogProps, setConnectDialogProps] = useState(
    ![TestStatus.TEST_LAUNCHED, TestStatus.TEST_INSESSION, TestStatus.TEST_PAUSED].includes(
      storedTestStatus
    )
      ? {
          title: t('components.websocketOverlays.awaitingConnectionTitle'),
          message: t('components.websocketOverlays.connecting'),
          showWaiting: true,
        }
      : TestStatus.TEST_PAUSED !== storedTestStatus
      ? {
          title: t('components.websocketOverlays.reconnecting'),
          message: t('components.websocketOverlays.connecting'),
          showWaiting: true,
        }
      : undefined // we do not want to show awaiting or reconnecting on test pause
  );

  //actions for failed connection
  const AWAITING_CONNECTION_FAILED_ACTIONS = useMemo(() => {
    return [
      {
        label: t('common.ok'),
        handle: () => {
          //redirect back to login screen
          setExit(true);
        },
      },
    ];
  }, [setExit, t]);

  useEffect(() => {
    const maxReconnectAttemptsBeforeAuthOrReconnecting = parseInt(
      window.env.REACT_APP_WS_ATTEMPT_BEFORE_RECONNECTING_STATUS
    );
    switch (socketStatus) {
      case WebSocketStatus.INITIAL:
        if (
          [TestStatus.TEST_LAUNCHED, TestStatus.TEST_INSESSION, TestStatus.TEST_PAUSED].includes(
            storedTestStatus
          ) &&
          (saveErrorCount >= TestStatus.MIN_CONSECUTIVE_ERRORS ||
            __attemptNumber >= maxReconnectAttemptsBeforeAuthOrReconnecting)
        ) {
          setAwaitingConnection(false);
          updatePausedBy(TestStatus.PAUSED_BY_SYSTEM);
          setStorageItem(
            Storage.STORAGE_PAUSED_REASON,
            `${NETWORK_ERROR_ACTION.PREFIX}${NETWORK_ERROR_ACTION.WEBSOCKET.type}`,
          );
          setStorageItem(Storage.STORAGE_PROCTOR_RESUME_NEEDED, true);
          setOpenErrorDialog(true);
        } else if (
          [TestStatus.TEST_LAUNCHED, TestStatus.TEST_INSESSION].includes(storedTestStatus) &&
          (saveErrorCount >= 0 || __attemptNumber >= 0)
        ) {
          setAwaitingConnection(false);
        } else {
          setConnectDialogProps({
            title: t('components.websocketOverlays.awaitingConnectionTitle'),
            message: t('components.websocketOverlays.connecting'),
            showWaiting: true,
          });
        }
        break;
      case WebSocketStatus.OPEN:
        setAwaitingConnection(false);
        if (
          ![TestStatus.TEST_ABORT, TestStatus.TEST_DUPLICATE].includes(
            storedTestStatus
          ) &&
          openErrorDialog &&
          saveErrorCount === 0
        ) {
          //Set submit In progress to undefined to make sure
          //Learnosity error save dialog does not show up in case the
          //user already submitted
          setSubmitInProgress();
          if (checkReadyToResume()) {
            sendMsgToWebsocket(
              constructMessage(
                PSYCHOMETRIC_EVENTS.READY_TO_RESUME,
                {
                  user: PwaEvents.ACTOR_SYSTEM,
                },
                learnosityRequestObj,
                loginData,
              ),
              false,
            );
          }
          setOpenErrorDialog(false);
          setOpenContinueDialog(true);
        }
        break;
      case WebSocketStatus.FAILED_TO_CONNECT:
        if ([TestStatus.TEST_LAUNCHED, TestStatus.TEST_INSESSION].includes(storedTestStatus)) {
          setConnectDialogProps({
            title: t('components.websocketOverlays.reconnecting'),
            message: t('components.websocketOverlays.couldNotReconnect'),
            showWaiting: false,
            actions: AWAITING_CONNECTION_FAILED_ACTIONS,
          });
        } else {
          setConnectDialogProps({
            title: t('components.websocketOverlays.awaitingConnectionTitle'),
            message: t('components.websocketOverlays.failedToEstablish'),
            showWaiting: false,
            actions: AWAITING_CONNECTION_FAILED_ACTIONS,
          });
        }
        break;
      case WebSocketStatus.RECONNECTING:
        setAwaitingConnection(false);
        updatePausedBy(TestStatus.PAUSED_BY_SYSTEM);
        setStorageItem(
          Storage.STORAGE_PAUSED_REASON,
          `${NETWORK_ERROR_ACTION.PREFIX}${NETWORK_ERROR_ACTION.WEBSOCKET.type}`,
        );
        setStorageItem(Storage.STORAGE_PROCTOR_RESUME_NEEDED, true);
        setOpenErrorDialog(true);
        break;
      case WebSocketStatus.FAILED_TO_RECONNECT:
        setAwaitingConnection(true);
        setConnectDialogProps({
          title: t('components.websocketOverlays.failedToEstablish'),
          message: t('components.websocketOverlays.couldNotReconnect'),
          showWaiting: false,
          actions: AWAITING_CONNECTION_FAILED_ACTIONS,
        });
        break;

      default:
        break;
    }
  }, [
    socketStatus,
    storedTestStatus,
    AWAITING_CONNECTION_FAILED_ACTIONS,
    setOpenErrorDialog,
    openErrorDialog,
    setOpenContinueDialog,
    __attemptNumber,
    saveErrorCount,
    learnosityItemApp,
    updatePausedBy,
    getStorageItem,
    setStorageItem,
    setSubmitInProgress,
    sendMsgToWebsocket,
    learnosityRequestObj,
    loginData,
    t
  ]);

  return (
    <CustomDialog open={awaitingConnection} dialogProps={connectDialogProps} />
  );
};

export default WebsocketOverlays;
